import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Formsy from 'formsy-react';
import RaisedButton from 'material-ui/RaisedButton';
import { Box, Typography } from '@material-ui/core';
import LinearProgress from 'material-ui/LinearProgress';
import Snackbar from 'material-ui/Snackbar';
import Spinner from '../../common/Spinner/Spinner';
import { raisedButtonStyle } from '../../../theme/AllocateBaseTheme';
import _ from '../../../utils/lodash';
import globals from '../../../globals/communityGlobals';
import { doubleActionPrevent } from '../../../utils/helpers';
import KeyboardDateSelector from '../../common/KeyboardDateTimeSelectors/KeyboardDateSelector';
import { isDateFromBeforeDateTo, isValidDate } from '../../../utils/dates';

const locale = globals.getArea('admin');
const errors = globals.getArea('error');

const idleStatus = 'Idle';
const pendingExecutionStatus = 'PendingExecution';
const newSynchronisation = 'New';

const datesToFormat = [
  'LastFullSynchronizationStart',
  'LastFullSynchronizationCompleted',
  'HeartBeat',
  'LastProcessedChangesetDate',
];

class SettingsNSync extends PureComponent {
  state = {
    open: false,
    dateFrom: null,
    dateTo: null,
  };
  componentDidMount() {
    this.props.getSysInfo();
    this.props.getSyncDetails();
    this.startUpdates(5000);
  }

  componentWillReceiveProps(nextProps) {
    const { SynchronizationStatus: currentStatus } = this.props.syncDetails || {};
    const { SynchronizationStatus: nextStatus } = nextProps.syncDetails || {};
    if (currentStatus && nextStatus && currentStatus !== nextStatus && nextStatus === idleStatus) {
      this.startUpdates(5000);
      this.setState({ open: true });
      // this.props.table.getData();
    }
  }

  componentWillUnmount() {
    clearInterval(this.intervalId);
  }

  startUpdates = interval => {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    this.intervalId = setInterval(this.periodicUpdate, interval);
  };

  periodicUpdate = () => {
    const { getSyncDetails, isSyncDetailsFetching } = this.props;
    if (!isSyncDetailsFetching) {
      getSyncDetails();
    }
  };

  handleSynchronize = doubleActionPrevent.bind(this)('Synchronize', async model => {
    const formattedDateFrom = model.from ? moment(model.from).format(globals.dateFormats.localApiFormat) : null;
    const formattedDateTo = model.to ? moment(model.to).format(globals.dateFormats.localApiFormat) : null;
    this.startUpdates(750);
    this.props.handleSynchronize(formattedDateFrom, formattedDateTo);
  });

  handleRequestClose = () => this.setState({ open: false });

  canSynchronize = () => {
    const { canTriggerSync, syncDetails } = this.props;
    const { open, dateFrom, dateTo } = this.state;

    return (
      canTriggerSync &&
      !open &&
      syncDetails &&
      syncDetails.SynchronizationStatus === idleStatus &&
      isValidDate(dateFrom, true) &&
      isValidDate(dateTo, true) &&
      isDateFromBeforeDateTo(dateFrom, dateTo, true)
    );
  };

  printDate = date => (date ? moment(date).format(globals.dateFormats.shortWithLongTime) : date);

  generateKeyValueRows = (object, identifier) => {
    const objectEntries = _.entries(object).sort((a, b) => {
      if (a[0] < b[0]) return -1;
      if (a[0] > b[0]) return 1;
      return 0;
    });
    return objectEntries.map(keyPair => (
      <tr key={identifier + keyPair[0]}>
        <td>{keyPair[0]}</td>
        <td>{datesToFormat.includes(keyPair[0]) ? this.printDate(keyPair[1]) : keyPair[1]}</td>
      </tr>
    ));
  };

  generateKeyValueTable = (object, identifier) => (
    <Box display="flex" px={2}>
      <Box flexGrow={1}>
        <table className="admin-key-value-table">
          <thead>
            <tr>
              <th>{locale.getMsg('field')}</th>
              <th>{locale.getMsg('value')}</th>
            </tr>
          </thead>
          <tbody>{this.generateKeyValueRows(object, identifier)}</tbody>
        </table>
      </Box>
    </Box>
  );

  render() {
    const { sysInfo, syncDetails, isSysInfoFetching } = this.props;
    const {
      SynchronizationProgress: synchronizationProgress,
      SynchronizationStatus: synchronizationStatus,
      ...restOfSyncDetails
    } = syncDetails || {};
    const isNewSynch = !!sysInfo && sysInfo.SyncMode === newSynchronisation;
    const syncProgressText = () =>
      restOfSyncDetails.SynchronizationStepInProgress
        ? locale.getMsg(restOfSyncDetails.SynchronizationStepInProgress.replace(/^\w/, c => c.toLowerCase()))
        : '';

    return (
      <div>
        <Snackbar
          open={this.state.open}
          message={locale.getMsg('synchronizationFinished')}
          autoHideDuration={4000}
          onRequestClose={this.handleRequestClose}
        />
        <Box px={2} pt={1}>
          <Typography variant="h3" px={2}>
            {locale.getMsg('systemSettings')}
          </Typography>
        </Box>
        {isSysInfoFetching ? <Spinner /> : this.generateKeyValueTable(sysInfo, 'systemSettingRow')}
        <hr />
        <h4>{locale.getMsg('synchronization')}</h4>
        {!syncDetails ? (
          <Spinner />
        ) : (
          <div className="admin-sync-status-panel">
            {this.generateKeyValueTable(restOfSyncDetails, 'synchronizationDetailsRow')}
            <div>
              {synchronizationStatus !== idleStatus && (
                <div>
                  <h5 className="centered-header">{locale.getMsg('synchronizationProgress')}</h5>
                  {!isNewSynch && (
                    <div className="row progress-row">
                      <div className="col-c8">
                        <div className="progress-box">
                          <LinearProgress
                            mode="determinate"
                            value={parseInt(
                              synchronizationStatus !== pendingExecutionStatus ? synchronizationProgress : 0,
                              10,
                            )}
                            style={{ height: '6px', backgroundColor: '#bdbdbd' }}
                          />
                          <h5>{`${
                            synchronizationStatus !== pendingExecutionStatus ? synchronizationProgress : 0
                          }%`}</h5>
                        </div>
                      </div>
                    </div>
                  )}
                  {isNewSynch && <h4 className="centered-header">{syncProgressText}</h4>}
                </div>
              )}
            </div>
          </div>
        )}
        <Formsy.Form onSubmit={this.handleSynchronize}>
          <div className="row">
            <div className="systemInfo-buttons">
              <div className="col-c2">
                <KeyboardDateSelector
                  name="from"
                  label={locale.getMsg('synchronizeFrom')}
                  value={this.state.dateFrom}
                  onChange={date => this.setState({ dateFrom: date })}
                />
              </div>
              <div className="col-c2">
                <KeyboardDateSelector
                  name="to"
                  label={locale.getMsg('synchronizeTo')}
                  value={this.state.dateTo}
                  onChange={date => this.setState({ dateTo: date })}
                  minDate={this.state.dateFrom}
                  minDateMessage={errors.getMsg('cannotBeEarlier', [locale.getMsg('synchronizeFrom')])}
                />
              </div>
              <div className="col-c3">
                <h5 className="centered-header">
                  {locale.getMsg('status')} <span>{synchronizationStatus}</span>
                </h5>
                <RaisedButton
                  type="submit"
                  label={locale.getMsg('synchronizeNow')}
                  disabled={!this.canSynchronize()}
                  labelStyle={{ ...raisedButtonStyle.labelStyle, lineHeight: '45px', fontSize: '1rem' }}
                  style={{ height: '45px' }}
                  fullWidth
                  primary
                />
              </div>
              <div className="col-c6">
                <span style={{ color: 'red' }}>{this.state.errorMessage}</span>
              </div>
            </div>
          </div>
        </Formsy.Form>
      </div>
    );
  }
}

SettingsNSync.defaultProps = {
  sysInfo: null,
  syncDetails: null,
  isSysInfoFetching: true,
  isSyncDetailsFetching: true,
  table: null,
};

SettingsNSync.propTypes = {
  getSysInfo: PropTypes.func.isRequired,
  getSyncDetails: PropTypes.func.isRequired,
  syncDetails: PropTypes.objectOf(PropTypes.string),
  sysInfo: PropTypes.objectOf(PropTypes.string),
  handleSynchronize: PropTypes.func.isRequired,
  isSysInfoFetching: PropTypes.bool,
  canTriggerSync: PropTypes.bool.isRequired,
  isSyncDetailsFetching: PropTypes.bool,
};

export default SettingsNSync;
